import React, { useEffect, useMemo, useState } from "react"

import { NetworkStatusRulesBulkUploadPopupComponent } from "one-verify/src/components/popups/networkStatusRulesBulkUploadPopup.component.tsx"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { NIRVANA_COLORS } from "@front-shared/config/theme.config.ts"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper.ts"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { NetworkStatusHelper } from "../../../helpers/networkStatus.helper"
import { useScrollableElementBoundaries } from "../../../hooks/scrollableElementBoundaries.hook.ts"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { networkStatusSelector } from "../../../store/selectors/networkStatus.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { networkStatusSetFilters } from "../../../store/slices/networkStatus.slice"
import type {
    INetworkStatusRulesFilters,
    IReviewNewRulesLocationState,
} from "../../../types/networkStatus.types"
import { NetworkStatusRulesFilterPopupComponent } from "../../popups/networkStatusRulesFilterPopup.component"
import { NetworkStatusRulesFiltersDisplayComponent } from "../filters/networkStatusRulesFiltersDisplay.component"
import { ActiveVersionSelectorComponent } from "./activeVersionSelector.component.tsx"
import { RulesListCsvDropdownComponent } from "./rulesListCsvDropdown.component.tsx"

export const RulesListHeaderComponent: React.FunctionComponent<
    IRulesListHeaderComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const networkStatusState = useAppSelector(networkStatusSelector)

    const scrollableTableBoundaries = useScrollableElementBoundaries(
        props.scrollableTableRef,
    )

    const [isFiltersPopupActive, setIsFiltersPopupActive] =
        useState<boolean>(false)

    const [isBulkUploadPopupActive, setIsBulkUploadPopupActive] =
        useState<boolean>(false)

    const filtersCount = useMemo<number>(() => {
        return NetworkStatusHelper.getMappedDisplayFiltersForRules(
            networkStatusState.filters,
        ).length
    }, [networkStatusState.filters])

    // Set default stored in session storage filters
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        const stored = BrowserStorageHelper.get<INetworkStatusRulesFilters>(
            GENERAL_CONFIG.browserStorageKeys.networkStatusRulesFilters,
            undefined,
            BrowserStorageType.sessionStorage,
        )

        if (stored && Object.keys(stored).length > 0) {
            dispatch(networkStatusSetFilters(stored))
        }
    }, [selectedPracticeRole?.id])

    const onFiltersSubmitted = (data: INetworkStatusRulesFilters) => {
        dispatch(networkStatusSetFilters(data))
    }

    const onRemoveFilter = (key: keyof INetworkStatusRulesFilters) => {
        const next = { ...networkStatusState.filters }

        delete next[key]

        if (key === "payerId") {
            delete next.payer
        }

        if (key === "thirdPartyAdministratorId") {
            delete next.thirdPartyAdministrator
        }

        onFiltersSubmitted(next)
    }

    return (
        <div
            className={`relative px-[24px] py-[12px] ${props.className ?? ""}`}
            style={{
                right: scrollableTableBoundaries?.x,
            }}
        >
            <div className="flex items-center justify-between w-full flex-wrap gap-y-2">
                <div className="flex items-center flex-wrap">
                    <PrimaryText
                        className="mr-[16px]"
                        size={29}
                        lineHeight={35}
                        weight="semibold"
                    >
                        Network Status Rules
                    </PrimaryText>

                    <div
                        className="
                            cursor-pointer rounded-[22px] px-[16px] py-[13px] bg-brand-warmLight
                            flex items-center justify-center
                            border border-solid border-brand-primary
                            hover:bg-brand-lilacLight
                        "
                        onClick={() => {
                            setIsFiltersPopupActive(true)
                        }}
                    >
                        <PrimaryText
                            typography="buttonText"
                            className="relative top-[2px]"
                        >
                            Filters
                        </PrimaryText>

                        {filtersCount ? (
                            <PrimaryText
                                className="ml-[8px] rounded-full bg-brand-primary w-[16px] h-[16px] flex items-center justify-center"
                                size={13}
                                lineHeight={13}
                                weight="bold"
                                color={NIRVANA_COLORS.brand.warmLight}
                            >
                                {filtersCount}
                            </PrimaryText>
                        ) : null}
                    </div>

                    <RulesListCsvDropdownComponent
                        selectedRulesVersion={props.selectedRulesVersion}
                        onOpenBulkUpload={() => {
                            setIsBulkUploadPopupActive(true)
                        }}
                    />
                </div>

                {/*hiding version selector for now, versioning concept will be removed in Coverage API implementation*/}
                <div className="hidden">
                    <ActiveVersionSelectorComponent
                        selectedRulesVersion={props.selectedRulesVersion}
                        onRulesVersionSelected={props.onRulesVersionSelected}
                    />
                </div>
            </div>

            <NetworkStatusRulesFiltersDisplayComponent
                className="mt-[12px]"
                filters={networkStatusState.filters}
                onRemoveFilter={onRemoveFilter}
            />

            {isFiltersPopupActive && (
                <NetworkStatusRulesFilterPopupComponent
                    isActive
                    existingFilters={networkStatusState.filters}
                    onFiltersSubmitted={onFiltersSubmitted}
                    onDone={() => {
                        setIsFiltersPopupActive(false)
                    }}
                />
            )}

            {isBulkUploadPopupActive && (
                <NetworkStatusRulesBulkUploadPopupComponent
                    isActive
                    onDone={() => {
                        setIsBulkUploadPopupActive(false)
                    }}
                    onValidationDone={data => {
                        setIsBulkUploadPopupActive(false)
                        props.onUploadWithErrors(data)
                    }}
                />
            )}
        </div>
    )
}

interface IRulesListHeaderComponentProps {
    className?: string
    scrollableTableRef?: React.RefObject<HTMLDivElement>
    onUploadWithErrors: (data: IReviewNewRulesLocationState) => void
    selectedRulesVersion: number | null
    onRulesVersionSelected: (version: number) => void
}
